import React, { useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { PageSEO } from '../components/seo/SchemaMarkup';
import { StrikingDistanceWidget } from '../components/insights/StrikingDistanceWidget';

/**
 * SEO Insights page — hosts the Overrank.ai embed (data-site="ninjathlete.com",
 * data-path="/insights", data-theme="dark"). The embed populates the
 * #ms-blog-content div once its script has finished loading.
 *
 * The <script> is injected imperatively in a useEffect so it runs AFTER the
 * mount point is in the DOM. We tag it with `data-overrank-embed` so hot
 * reloads or route changes can clean up cleanly.
 */
const OVERRANK_SCRIPT_ID = 'overrank-embed-script';

const InsightsPage = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    // If script is already on the page (client-side re-visit), skip re-injecting.
    if (document.getElementById(OVERRANK_SCRIPT_ID)) {
      return undefined;
    }

    const script = document.createElement('script');
    script.id = OVERRANK_SCRIPT_ID;
    script.src = 'https://www.overrank.ai/embed.js';
    script.async = true;
    script.setAttribute('data-site', 'ninjathlete.com');
    script.setAttribute('data-path', '/insights');
    script.setAttribute('data-theme', 'dark');
    script.setAttribute('data-overrank-embed', 'true');
    document.body.appendChild(script);

    return () => {
      // Cleanup on unmount so a fresh visit re-initializes the embed.
      const node = document.getElementById(OVERRANK_SCRIPT_ID);
      if (node) node.remove();
    };
  }, []);

  return (
    <main className="pt-20 min-h-screen bg-black text-white" data-testid="insights-page">
      <PageSEO
        title="SEO Insights & Content Trends | NinjAthlete"
        description="Data-driven insights, trending queries, and content intelligence for the peptide, HYROX, and longevity communities — curated with Overrank.ai."
        path="/insights"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Insights', url: '/insights' },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0a0a0a] to-black" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 70% 20%, rgba(199,250,62,0.35) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(0,240,255,0.18) 0%, transparent 50%)',
          }}
        />
        <div className="container-ninja relative z-10 py-14">
          <div className="max-w-3xl">
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#C7FA3E] text-black text-xs font-heading tracking-widest uppercase mb-6"
              data-testid="insights-badge"
            >
              <Sparkles className="w-3.5 h-3.5" /> Live Insights
            </span>
            <h1
              className="text-4xl sm:text-5xl md:text-6xl text-white font-heading leading-[0.95] mb-5"
              data-testid="insights-hero-title"
            >
              WHAT THE <span className="text-[#C7FA3E]">DATA</span> IS SAYING
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl max-w-xl">
              Real-time SEO intelligence, trending search queries, and content
              gaps across peptides, HYROX, and longevity — powered by Overrank.
            </p>
          </div>
        </div>
      </section>

      {/* Embed container */}
      <section className="container-ninja py-10">
        <div
          id="ms-blog-content"
          ref={mountRef}
          data-testid="overrank-embed"
          className="min-h-[400px]"
        >
          <div className="flex items-center gap-3 text-zinc-500 text-sm py-8">
            <div className="w-3 h-3 rounded-full bg-[#C7FA3E] animate-pulse" />
            Loading insights…
          </div>
        </div>
      </section>

      {/* Striking Distance — self-serve SEO command center */}
      <section className="container-ninja pb-16">
        <StrikingDistanceWidget />
      </section>
    </main>
  );
};

export default InsightsPage;
